// src/pages/workflow/ExecutionDetailPage.tsx
// Pipeline execution detail with node-by-node trace visualization.

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';

import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  ErrorAlert,
  Input,
  Textarea,
} from '@react/components';
import { Modal } from '@/features/workflow/Modal';
import {
  executionStatusMap,
  getEntityId,
  formatDateTime,
  formatDuration,
  getStatusColor,
} from '@/features/workflow/logic';

// --- Icons ---

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  );
}

function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (
    <svg
      className={`${className} transition-transform ${open ? 'rotate-90' : ''}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// Node execution status dot
function StatusDot({ status }: { status: string }) {
  const color = getStatusColor(status);
  const isRunning = status === 'EXECUTION_STATUS_RUNNING';
  return (
    <span className="relative flex h-3 w-3">
      {isRunning && (
        <span
          className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
          style={{ backgroundColor: color }}
        />
      )}
      <span
        className="relative inline-flex rounded-full h-3 w-3"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

// --- Pulsing live indicator ---
function LiveIndicator() {
  return (
    <span className="inline-flex items-center gap-1.5 ml-2">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
      </span>
      <span className="text-xs text-blue-500 font-medium">LIVE</span>
    </span>
  );
}

// --- Visual Execution Flow (horizontal mini-graph) ---
function ExecutionFlowGraph({ nodeExecs }: { nodeExecs: any[] }) {
  if (!nodeExecs || nodeExecs.length === 0) return null;

  const nodeWidth = 100;
  const connectorWidth = 24;
  const totalWidth = nodeExecs.length * (nodeWidth + connectorWidth) - connectorWidth + 32;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="overflow-x-auto">
          <div className="flex items-center gap-0 py-2" style={{ minWidth: totalWidth }}>
            {nodeExecs.map((ne: any, idx: number) => {
              const status = ne.status || 'EXECUTION_STATUS_PENDING';
              const color = getStatusColor(status);
              const isFailed = status === 'EXECUTION_STATUS_FAILED';
              const isRunning = status === 'EXECUTION_STATUS_RUNNING';
              const isCompleted = status === 'EXECUTION_STATUS_COMPLETED';
              const isSkipped = status === 'EXECUTION_STATUS_CANCELLED' || status === 'EXECUTION_STATUS_PENDING';

              let borderClass = 'border-border';
              let bgClass = 'bg-background';
              if (isFailed) { borderClass = 'border-red-400'; bgClass = 'bg-red-50 dark:bg-red-950/30'; }
              else if (isCompleted) { borderClass = 'border-green-400'; bgClass = 'bg-green-50 dark:bg-green-950/30'; }
              else if (isRunning) { borderClass = 'border-blue-400'; bgClass = 'bg-blue-50 dark:bg-blue-950/30'; }
              else if (isSkipped) { borderClass = 'border-border'; bgClass = 'bg-muted/50'; }

              return (
                <div key={ne.node_id + idx} className="flex items-center flex-shrink-0">
                  {/* Node rectangle */}
                  <div
                    className={`relative flex items-center gap-2 px-3 py-1.5 rounded-md border-2 ${borderClass} ${bgClass} transition-all`}
                    style={{ minWidth: nodeWidth }}
                  >
                    {isRunning && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
                      </span>
                    )}
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className={`text-xs font-medium truncate ${isSkipped ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {ne.node_id}
                    </span>
                  </div>
                  {/* Connector line */}
                  {idx < nodeExecs.length - 1 && (
                    <div className="flex items-center flex-shrink-0" style={{ width: connectorWidth }}>
                      <svg width={connectorWidth} height="12" className="overflow-visible">
                        <line x1="0" y1="6" x2={connectorWidth} y2="6" stroke="var(--border)" strokeWidth="2" />
                        <polygon
                          points={`${connectorWidth - 4},2 ${connectorWidth},6 ${connectorWidth - 4},10`}
                          fill="var(--border)"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Timing Waterfall ---
function TimingWaterfall({ nodeExecs, totalDuration }: { nodeExecs: any[]; totalDuration: number }) {
  if (!nodeExecs || nodeExecs.length === 0 || !totalDuration) return null;

  const maxDuration = Math.max(totalDuration, ...nodeExecs.map((ne: any) => ne.duration_ms || 0));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Timing Waterfall</CardTitle>
          <span className="text-sm text-muted-foreground font-mono">
            {formatDuration(totalDuration)} total
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="space-y-1.5">
          {nodeExecs.map((ne: any, idx: number) => {
            const duration = ne.duration_ms || 0;
            const pct = maxDuration > 0 ? Math.max((duration / maxDuration) * 100, 1) : 1;
            const status = ne.status || 'EXECUTION_STATUS_PENDING';
            const color = getStatusColor(status);

            return (
              <div key={ne.node_id + idx} className="flex items-center gap-3 group">
                <span className="text-xs text-muted-foreground w-24 truncate flex-shrink-0 text-right font-mono">
                  {ne.node_id}
                </span>
                <div className="flex-1 h-5 bg-muted/30 rounded-sm overflow-hidden relative">
                  <div
                    className="h-full rounded-sm transition-all duration-300 flex items-center"
                    style={{ width: `${pct}%`, backgroundColor: color, minWidth: 2 }}
                  >
                    {pct > 15 && (
                      <span className="text-[10px] font-mono text-white px-1.5 truncate">
                        {formatDuration(duration)}
                      </span>
                    )}
                  </div>
                  {pct <= 15 && (
                    <span className="absolute left-full ml-1 text-[10px] font-mono text-muted-foreground top-1/2 -translate-y-1/2">
                      {formatDuration(duration)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// --- JSON Syntax Highlighted Viewer ---
function JsonViewer({ data, title }: { data: string; title: string }) {
  const [copied, setCopied] = useState(false);

  const formatted = useMemo(() => {
    if (!data) return '';
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      return data;
    }
  }, [data]);

  const highlighted = useMemo(() => {
    if (!formatted) return '';
    // Simple syntax highlighting with Tailwind-friendly spans
    return formatted
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Strings (keys and values)
      .replace(/"([^"\\]*(\\.[^"\\]*)*)"\s*:/g, '<span class="text-purple-600 dark:text-purple-400">"$1"</span>:')
      .replace(/:\s*"([^"\\]*(\\.[^"\\]*)*)"/g, ': <span class="text-green-600 dark:text-green-400">"$1"</span>')
      // Standalone string values in arrays
      .replace(/(?<=[\[,]\s*)"([^"\\]*(\\.[^"\\]*)*)"/g, '<span class="text-green-600 dark:text-green-400">"$1"</span>')
      // Numbers
      .replace(/:\s*(-?\d+\.?\d*)/g, ': <span class="text-blue-600 dark:text-blue-400">$1</span>')
      // Booleans & null
      .replace(/:\s*(true|false|null)/g, ': <span class="text-amber-600 dark:text-amber-400">$1</span>');
  }, [formatted]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formatted);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }, [formatted]);

  if (!data) return null;

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <h5 className="text-xs font-semibold uppercase text-muted-foreground">{title}</h5>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-muted"
        >
          {copied ? (
            <>
              <CheckIcon className="h-3 w-3 text-green-500" />
              <span className="text-green-500">Copied</span>
            </>
          ) : (
            <>
              <CopyIcon className="h-3 w-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre
        className="bg-muted rounded-md p-3 text-xs font-mono overflow-x-auto max-h-48 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </div>
  );
}

// --- AI node output rendering ---
// LLM nodes output {text|category|data|summary|translation, usage:{...}};
// agent runs add {answer, iterations:[{tool, arguments, result|error}, ...]}.
// Render those readable instead of one opaque JSON blob; raw JSON stays
// available behind a disclosure.

function tryParseJson(data: any): any {
  if (typeof data !== 'string' || !data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

function prettyJson(value: any): string {
  if (typeof value === 'string') {
    const parsed = tryParseJson(value);
    if (parsed !== null && typeof parsed === 'object') return JSON.stringify(parsed, null, 2);
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// The plain-language part of an AI output, if it has one.
function aiTextOf(output: any): string | null {
  if (!output || typeof output !== 'object') return null;
  for (const key of ['answer', 'text', 'summary', 'translation', 'category']) {
    if (typeof output[key] === 'string' && output[key]) return output[key];
  }
  return null;
}

// Gate on the AI-specific fields (usage, iterations, answer) rather than
// generic ones like `text`, so ordinary nodes keep the plain JSON view.
function isAiOutput(output: any): boolean {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return false;
  return Boolean(output.usage || Array.isArray(output.iterations) || typeof output.answer === 'string');
}

function TokenUsage({ usage }: { usage: any }) {
  const { t } = useI18n();
  if (!usage || typeof usage !== 'object') return null;
  const parts: Array<[number, string]> = [];
  if (typeof usage.prompt_tokens === 'number') parts.push([usage.prompt_tokens, t('workflow.ai.promptTokens')]);
  if (typeof usage.completion_tokens === 'number') parts.push([usage.completion_tokens, t('workflow.ai.completionTokens')]);
  if (typeof usage.total_tokens === 'number') parts.push([usage.total_tokens, t('workflow.ai.totalTokens')]);
  if (parts.length === 0) return null;
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
      <span className="font-semibold uppercase">{t('workflow.ai.tokens')}</span>
      {parts.map(([value, label], idx) => (
        <span key={idx}>
          {value} {label}
        </span>
      ))}
    </div>
  );
}

function AgentIterations({ iterations }: { iterations: any[] }) {
  const { t } = useI18n();
  return (
    <div>
      <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">
        {t('workflow.ai.steps')} ({iterations.length})
      </h5>
      <ol className="space-y-1.5">
        {iterations.map((it: any, idx: number) => {
          const tool = it.tool || it.tool_name || it.name || '';
          const hasError = Boolean(it.error);
          return (
            <li key={idx} className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground flex-shrink-0">
                  {typeof it.iteration === 'number' ? it.iteration : idx + 1}
                </span>
                {tool ? (
                  <code className="font-mono text-xs text-foreground truncate">{tool}</code>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t('workflow.ai.step')} {idx + 1}
                  </span>
                )}
                {hasError && (
                  <Badge variant="error" size="sm">
                    {t('workflow.ai.error')}
                  </Badge>
                )}
              </div>
              {it.arguments !== undefined && it.arguments !== null && (
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {t('workflow.ai.arguments')}
                  </summary>
                  <pre className="mt-1 bg-muted rounded-md p-2 text-xs font-mono overflow-x-auto max-h-40">
                    {prettyJson(it.arguments)}
                  </pre>
                </details>
              )}
              {hasError ? (
                <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap">{String(it.error)}</p>
              ) : (
                it.result !== undefined &&
                it.result !== null && (
                  <details className="mt-1.5">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
                      {t('workflow.ai.result')}
                    </summary>
                    <pre className="mt-1 bg-muted rounded-md p-2 text-xs font-mono overflow-x-auto max-h-40">
                      {prettyJson(it.result)}
                    </pre>
                  </details>
                )
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function AiOutputView({ data, title }: { data: string; title: string }) {
  const { t } = useI18n();
  const output = useMemo(() => tryParseJson(data), [data]);
  const text = aiTextOf(output);
  const iterations = Array.isArray(output?.iterations) ? output.iterations : null;

  return (
    <div className="flex-1 min-w-0 space-y-3">
      {title && <h5 className="text-xs font-semibold uppercase text-muted-foreground">{title}</h5>}
      {text && (
        <div className="bg-muted rounded-md p-3 text-sm text-foreground whitespace-pre-wrap max-h-60 overflow-y-auto">
          {text}
        </div>
      )}
      {iterations && iterations.length > 0 && <AgentIterations iterations={iterations} />}
      <TokenUsage usage={output?.usage} />
      <details>
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
          {t('workflow.ai.rawOutput')}
        </summary>
        <div className="mt-1">
          <JsonViewer data={data} title="" />
        </div>
      </details>
    </div>
  );
}

export const ExecutionDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useWorkflow();
  const { t, locale } = useI18n();

  const [execution, setExecution] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const traceRef = useRef<EventSource | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Save this run as a test case — CaptureTestCase.
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureName, setCaptureName] = useState('');
  const [captureDescription, setCaptureDescription] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [captureResult, setCaptureResult] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    loadExecution();
    loadLogs();
    return () => {
      traceRef.current?.close();
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [id]);

  // --- Live refresh for running/pending executions ---
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    const status = execution?.status;
    const isLive = status === 'EXECUTION_STATUS_RUNNING' || status === 'EXECUTION_STATUS_PENDING';

    if (isLive && id) {
      refreshIntervalRef.current = setInterval(() => {
        loadExecution();
        loadLogs();
      }, 3000);
    }

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [execution?.status, id]);

  const loadExecution = async () => {
    setIsLoading(true);
    try {
      const result = await store.GetExecution?.(id!);
      setExecution(result || store.currentItem);
    } catch {
      // Error handled by store
    } finally {
      setIsLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const result = await store.GetExecutionLogs?.(id!);
      setLogs((result as any)?.items || []);
    } catch {
      // Non-critical
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    await store.CancelExecution?.(id, {});
    loadExecution();
  };

  const handleRetry = async () => {
    if (!id) return;
    const result = await store.RetryExecution?.(id, {});
    const newId = getEntityId(result);
    if (newId) window.location.href = `/workflows/executions/${newId}`;
    else loadExecution();
  };

  const handlePause = async () => {
    if (!id) return;
    await store.PauseExecution?.(id, {});
    loadExecution();
  };

  const handleResume = async () => {
    if (!id) return;
    await store.ResumeExecution?.(id, {});
    loadExecution();
  };

  const openCapture = () => {
    setCaptureName(`Captured ${new Date().toLocaleString()}`);
    setCaptureDescription('');
    setCaptureResult(null);
    setCaptureOpen(true);
  };

  const submitCapture = async () => {
    if (!id) return;
    setCapturing(true);
    try {
      const res = await store.CaptureTestCase?.(id, {
        name: captureName.trim() || undefined,
        description: captureDescription.trim() || undefined,
      });
      const tc = (res as any)?.test_case ?? res;
      setCaptureResult(tc?.name || t('workflow.captureTestCase.success'));
      setCaptureOpen(false);
    } catch {
      // Error surfaced via store.error
    } finally {
      setCapturing(false);
    }
  };

  const toggleNodeExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 w-full max-w-2xl">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-48 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/workflows" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const status = execution.status || 'EXECUTION_STATUS_PENDING';
  const statusInfo = executionStatusMap[status] || executionStatusMap.EXECUTION_STATUS_PENDING;
  const isActive = status === 'EXECUTION_STATUS_RUNNING' || status === 'EXECUTION_STATUS_PENDING';
  const isPaused = status === 'EXECUTION_STATUS_PAUSED';
  const isFailed = status === 'EXECUTION_STATUS_FAILED';
  const isLive = isActive;
  const nodeExecs = execution.node_executions || [];
  const pipelineId = execution.pipeline?.id;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        {pipelineId && (
          <>
            <Link to={`/workflows/${pipelineId}?tab=executions`} className="text-muted-foreground hover:text-foreground">
              {t('workflow.executions')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
          </>
        )}
        <span className="text-foreground font-mono">{id?.slice(0, 8)}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{t('workflow.executionDetail')}</h1>
            <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>
            {isLive && <LiveIndicator />}
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono">{id}</p>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <>
              <Button variant="secondary" size="sm" onClick={handlePause}>
                <PauseIcon className="h-3.5 w-3.5 mr-1.5" />{t('workflow.pause')}
              </Button>
              <Button variant="danger" size="sm" onClick={handleCancel}>
                <StopIcon className="h-3.5 w-3.5 mr-1.5" />{t('workflow.cancel')}
              </Button>
            </>
          )}
          {isPaused && (
            <Button size="sm" onClick={handleResume}>
              <PlayIcon className="h-3.5 w-3.5 mr-1.5" />{t('workflow.resume')}
            </Button>
          )}
          {isFailed && (
            <Button size="sm" onClick={handleRetry}>
              <RefreshIcon className="h-3.5 w-3.5 mr-1.5" />{t('workflow.retry')}
            </Button>
          )}
          {!isActive && !isPaused && (
            <Button variant="secondary" size="sm" onClick={openCapture}>
              {t('workflow.captureTestCase.cta')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={loadExecution}>
            <RefreshIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {captureResult && (
        <Alert variant="success" dismissible onDismiss={() => setCaptureResult(null)}>
          {t('workflow.captureTestCase.success')}: {captureResult}
        </Alert>
      )}

      {/* Error message. user_message explains the failure in the author's terms;
          the raw error is kept as secondary detail for operators. */}
      {(execution.user_message || execution.error) && (
        <Alert variant="error" title={t('workflow.executionError')}>
          {execution.user_message ? (
            <>
              <p className="text-sm">{execution.user_message}</p>
              {execution.remediation && (
                <p className="mt-2 text-sm">
                  <span className="font-medium">{t('workflow.whatToDo')}: </span>
                  {execution.remediation}
                </p>
              )}
              {execution.error && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-muted-foreground">
                    {t('workflow.technicalDetails')}
                  </summary>
                  <p className="mt-1 font-mono text-xs whitespace-pre-wrap">{execution.error}</p>
                </details>
              )}
            </>
          ) : (
            <p className="font-mono text-sm">{execution.error}</p>
          )}
        </Alert>
      )}

      {/* Suspended info */}
      {isPaused && execution.suspended_at_node_id && (
        <Alert variant="warning" title={t('workflow.suspendedAt')}>
          <p>{t('workflow.suspendedAtNode')}: <code className="font-mono">{execution.suspended_at_node_id}</code></p>
          {execution.wait_reason && <p className="mt-1">{execution.wait_reason}</p>}
        </Alert>
      )}

      {/* Visual execution flow graph */}
      <ExecutionFlowGraph nodeExecs={nodeExecs} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-foreground">{formatDuration(execution.duration_ms)}</p>
            <p className="text-xs text-muted-foreground">{t('workflow.duration')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-foreground">{nodeExecs.length}</p>
            <p className="text-xs text-muted-foreground">{t('workflow.nodesExecuted')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-foreground">
              {nodeExecs.filter((n: any) => n.status === 'EXECUTION_STATUS_COMPLETED').length}
            </p>
            <p className="text-xs text-muted-foreground">{t('workflow.nodesCompleted')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-foreground">
              {nodeExecs.filter((n: any) => n.status === 'EXECUTION_STATUS_FAILED').length}
            </p>
            <p className="text-xs text-muted-foreground">{t('workflow.nodesFailed')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-lg font-bold text-foreground">{execution.trigger_type || '-'}</p>
            <p className="text-xs text-muted-foreground">{t('workflow.trigger')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Timing Waterfall */}
      <TimingWaterfall nodeExecs={nodeExecs} totalDuration={execution.duration_ms} />

      {/* Node execution trace */}
      <Card>
        <CardHeader>
          <CardTitle>{t('workflow.nodeTrace')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {nodeExecs.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{t('workflow.noNodeExecutions')}</div>
          ) : (
            <div className="divide-y divide-border">
              {nodeExecs.map((ne: any, idx: number) => {
                const neStatus = ne.status || 'EXECUTION_STATUS_PENDING';
                const isExpanded = expandedNodes.has(ne.node_id);
                const neIsFailed = neStatus === 'EXECUTION_STATUS_FAILED';

                return (
                  <div key={ne.node_id + idx}>
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                      onClick={() => toggleNodeExpand(ne.node_id)}
                    >
                      <ChevronIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" open={isExpanded} />
                      <StatusDot status={neStatus} />
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-sm text-foreground">{ne.node_id}</span>
                        {ne.node_type_slug && (
                          <span className="text-xs text-muted-foreground ml-2">{ne.node_type_slug}</span>
                        )}
                      </div>

                      {/* Inline error for failed nodes (always visible, no need to expand) */}
                      {neIsFailed && (ne.user_message || ne.error) && (() => {
                        const summary = ne.user_message || ne.error;
                        return (
                          <span className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-2 py-0.5 rounded truncate max-w-xs hidden sm:inline-block">
                            {summary.length > 60 ? summary.slice(0, 60) + '...' : summary}
                          </span>
                        );
                      })()}

                      <span className="text-xs text-muted-foreground w-16 text-right font-mono flex-shrink-0">
                        {formatDuration(ne.duration_ms)}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 ml-11 space-y-3">
                        {/* Timing */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">{t('workflow.startedAt')}: </span>
                            <span className="text-foreground">{formatDateTime(ne.started_at, locale)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">{t('workflow.completedAt')}: </span>
                            <span className="text-foreground">{formatDateTime(ne.completed_at, locale)}</span>
                          </div>
                        </div>

                        {/* Error (expanded detail): lead with the explanation and
                            the next step, keep the raw error as supporting detail. */}
                        {(ne.user_message || ne.error) && (
                          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-md p-3 text-sm space-y-2">
                            {ne.user_message ? (
                              <>
                                <div>
                                  <span className="font-medium">{t('workflow.whatHappened')}: </span>
                                  {ne.user_message}
                                </div>
                                {ne.remediation && (
                                  <div>
                                    <span className="font-medium">{t('workflow.whatToDo')}: </span>
                                    {ne.remediation}
                                  </div>
                                )}
                                {ne.error_code && (
                                  <div className="text-xs opacity-75">
                                    {t('workflow.errorReference')}: <code className="font-mono">{ne.error_code}</code>
                                  </div>
                                )}
                                {ne.error && (
                                  <details>
                                    <summary className="cursor-pointer text-xs opacity-75">
                                      {t('workflow.technicalDetails')}
                                    </summary>
                                    <div className="mt-1 font-mono text-xs whitespace-pre-wrap">{ne.error}</div>
                                  </details>
                                )}
                              </>
                            ) : (
                              <div className="font-mono whitespace-pre-wrap">{ne.error}</div>
                            )}
                          </div>
                        )}

                        {/* Split view: Input left, Output right. AI node
                            outputs (usage / agent iterations / answer) get the
                            readable view instead of the raw JSON blob. */}
                        {(ne.input_data || ne.output_data) && (
                          <div className="flex gap-4 flex-col md:flex-row">
                            {ne.input_data && (
                              <JsonViewer data={ne.input_data} title={t('workflow.inputData')} />
                            )}
                            {ne.output_data &&
                              (isAiOutput(tryParseJson(ne.output_data)) ? (
                                <AiOutputView data={ne.output_data} title={t('workflow.outputData')} />
                              ) : (
                                <JsonViewer data={ne.output_data} title={t('workflow.outputData')} />
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Execution logs */}
      {logs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('workflow.executionLogs')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-80 overflow-y-auto">
              {logs.map((log: any, idx: number) => {
                const levelColors: Record<string, string> = {
                  ERROR: 'text-destructive',
                  WARN: 'text-amber-600',
                  INFO: 'text-blue-600',
                  DEBUG: 'text-muted-foreground',
                };
                return (
                  <div key={idx} className="flex items-start gap-3 px-4 py-1.5 text-xs font-mono hover:bg-muted/50 border-b border-border last:border-0">
                    <span className="text-muted-foreground w-20 flex-shrink-0">
                      {formatDateTime(log.timestamp, locale)}
                    </span>
                    <span className={`w-10 flex-shrink-0 font-semibold ${levelColors[log.level] || ''}`}>
                      {log.level}
                    </span>
                    <span className="text-muted-foreground w-20 flex-shrink-0 truncate">{log.node_id}</span>
                    <span className="text-foreground flex-1">{log.message}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execution metadata */}
      <Card>
        <CardHeader>
          <CardTitle>{t('workflow.executionInfo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-2 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('workflow.executionId')}</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{id}</dd>
            </div>
            {execution.pipeline?.id && (
              <div className="py-2 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('workflow.pipelineId')}</dt>
                <dd className="text-sm col-span-2">
                  <Link to={`/workflows/${execution.pipeline.id}`} className="text-primary hover:underline font-mono">
                    {execution.pipeline.id}
                  </Link>
                </dd>
              </div>
            )}
            <div className="py-2 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('workflow.executionMode')}</dt>
              <dd className="text-sm text-foreground col-span-2">{execution.execution_mode || 'NORMAL'}</dd>
            </div>
            <div className="py-2 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('workflow.startedAt')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDateTime(execution.started_at, locale)}</dd>
            </div>
            {execution.completed_at && (
              <div className="py-2 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('workflow.completedAt')}</dt>
                <dd className="text-sm text-foreground col-span-2">{formatDateTime(execution.completed_at, locale)}</dd>
              </div>
            )}
            {execution.trigger_data && (
              <div className="py-2 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('workflow.triggerData')}</dt>
                <dd className="text-sm col-span-2">
                  <JsonViewer data={execution.trigger_data} title="" />
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Save this run as a test case — CaptureTestCase */}
      <Modal
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        title={t('workflow.captureTestCase.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCaptureOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={submitCapture} isLoading={capturing} disabled={!captureName.trim()}>{t('workflow.captureTestCase.cta')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('workflow.captureTestCase.hint')}</p>
          <Input label={t('workflow.testName')} value={captureName} onChange={(e) => setCaptureName(e.target.value)} />
          <Textarea label={t('workflow.field.description')} value={captureDescription} onChange={(e) => setCaptureDescription(e.target.value)} rows={2} />
        </div>
      </Modal>
    </div>
  );
});

export default ExecutionDetailPage;
